export type NewsletterPublisher =
  | 'AP'
  | 'BT'
  | 'E24'
  | 'SA'
  | 'VKNO'
  | 'VG'
  | 'SVD'
  | 'AB'
  | 'VKSE'
  | 'OMNI'
  | 'VINK';

export type NewsletterSubscriptionStatus = {
  /**
   * The unique identifier of the newsletter.
   */
  id: string;
  /**
   * The status of the action performed on the newsletter.
   */
  status: 'subscribed' | 'unsubscribed';
  /**
   * Boolean value indicating whether the user is subscribed to the newsletter.
   */
  subscribed: boolean;
  /**
   * Boolean value indicating whether the newsletter status is cached.
   */
  cached: boolean;
};

class NewsLetterApiError extends Error {
  constructor(
    message: string,
    public response?: Response,
  ) {
    super(message);
    this.name = 'NewsLetterApiError';
  }
}

function isValidUUID(uuid: string): boolean {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  return uuidRegex.test(uuid);
}
function createNewsLetterApi(
  baseUrl: string,
  publication: NewsletterPublisher,
) {
  if (!baseUrl) {
    throw new Error('baseUrl is required');
  }

  if (!publication) {
    throw new Error('publication is required');
  }

  const baseApiUrl = `https://${new URL(baseUrl).host}`;

  async function fetchApi(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<Response> {
    const response = await fetch(
      `${baseApiUrl}/api/${publication}${endpoint}`,
      {
        credentials: 'include',
        ...options,
      },
    );

    if (!response.ok) {
      throw new NewsLetterApiError('API request failed', response);
    }

    return response;
  }

  async function getNewsletterSubscriptions(): Promise<
    NewsletterSubscriptionStatus[]
  > {
    const response = await fetchApi('/subscriptions');

    return response.json();
  }

  async function getSubscriptionStatus(
    newsletterId: string,
  ): Promise<NewsletterSubscriptionStatus> {
    if (!isValidUUID(newsletterId)) {
      throw new Error('Invalid newsletterId format');
    }
    const response = await fetchApi(`/${newsletterId}`);

    return response.json();
  }

  async function setSubscriptionStatus(
    newsletterId: string,
    newStatus: Pick<NewsletterSubscriptionStatus, 'status'>,
  ): Promise<NewsletterSubscriptionStatus> {
    if (!isValidUUID(newsletterId)) {
      throw new Error('Invalid newsletterId format');
    }
    const response = await fetchApi(`/${newsletterId}`, {
      method: 'PUT',
      body: JSON.stringify({ status: newStatus.status }),
    });

    return response.json();
  }

  async function subscribe(
    newsletterId: string,
  ): Promise<NewsletterSubscriptionStatus> {
    if (!isValidUUID(newsletterId)) {
      throw new Error('Invalid newsletterId format');
    }
    const response = await fetchApi(`/${newsletterId}/subscribe`, {
      method: 'POST',
    });

    return response.json();
  }

  async function unsubscribe(
    newsletterId: string,
  ): Promise<NewsletterSubscriptionStatus> {
    if (!isValidUUID(newsletterId)) {
      throw new Error('Invalid newsletterId format');
    }
    const response = await fetchApi(`/${newsletterId}/unsubscribe`, {
      method: 'POST',
    });

    return response.json();
  }

  return {
    getNewsletterSubscriptions,
    getSubscriptionStatus,
    setSubscriptionStatus,
    subscribe,
    unsubscribe,
  };
}

export { createNewsLetterApi };
